import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';

const FunctionsList = () => {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [functionForm, setFunctionForm] = useState({
    name: '',
    description: '',
    function_type: 'http_request',
    configuration: {
      url: '',
      method: 'POST',
      headers: {}
    },
    parameters: []
  });
  const [currentParameter, setCurrentParameter] = useState({
    name: '',
    description: '',
    required: false
  });
  const [openParameterDialog, setOpenParameterDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchFunctions();
  }, []);

  const fetchFunctions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/functions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFunctions(data);
      } else {
        console.error('Error fetching functions');
        showSnackbar('Error fetching functions', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error fetching functions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (func = null) => {
    if (func) {
      // Edit existing function
      setEditingFunction(func);
      setFunctionForm({
        name: func.name,
        description: func.description,
        function_type: func.function_type,
        configuration: { ...func.configuration },
        parameters: func.parameters || []
      });
    } else {
      // Create new function
      setEditingFunction(null);
      setFunctionForm({
        name: '',
        description: '',
        function_type: 'http_request',
        configuration: {
          url: '',
          method: 'POST',
          headers: {}
        },
        parameters: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFunction(null);
  };

  const handleInputChange = (field) => (event) => {
    setFunctionForm({
      ...functionForm,
      [field]: event.target.value
    });
  };

  const handleConfigChange = (field) => (event) => {
    setFunctionForm({
      ...functionForm,
      configuration: {
        ...functionForm.configuration,
        [field]: event.target.value
      }
    });
  };

  const handleOpenParameterDialog = (parameter = null) => {
    if (parameter) {
      // Edit existing parameter
      setCurrentParameter({ ...parameter });
    } else {
      // Add new parameter
      setCurrentParameter({
        name: '',
        description: '',
        required: false
      });
    }
    setOpenParameterDialog(true);
  };

  const handleCloseParameterDialog = () => {
    setOpenParameterDialog(false);
  };

  const handleParameterInputChange = (field) => (event) => {
    setCurrentParameter({
      ...currentParameter,
      [field]: field === 'required' ? event.target.checked : event.target.value
    });
  };

  const handleSaveParameter = () => {
    // Validate parameter
    if (!currentParameter.name || !currentParameter.description) {
      showSnackbar('Parameter name and description are required', 'error');
      return;
    }
    
    // Check if we're editing an existing parameter or adding a new one
    const existingIndex = functionForm.parameters.findIndex(p => p.name === currentParameter.name);
    
    if (existingIndex >= 0) {
      // Update existing parameter
      const updatedParameters = [...functionForm.parameters];
      updatedParameters[existingIndex] = currentParameter;
      setFunctionForm({
        ...functionForm,
        parameters: updatedParameters
      });
    } else {
      // Add new parameter
      setFunctionForm({
        ...functionForm,
        parameters: [...functionForm.parameters, currentParameter]
      });
    }
    
    handleCloseParameterDialog();
  };

  const handleDeleteParameter = (paramName) => {
    setFunctionForm({
      ...functionForm,
      parameters: functionForm.parameters.filter(p => p.name !== paramName)
    });
  };

  const handleSaveFunction = async () => {
    // Validate inputs
    if (!functionForm.name || !functionForm.description || 
        (functionForm.function_type === 'http_request' && !functionForm.configuration.url)) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    
    try {
      let url, method;
      
      if (editingFunction) {
        // Update existing function
        url = `${process.env.REACT_APP_API_URL}/api/functions/${editingFunction.id}`;
        method = 'PUT';
      } else {
        // Create new function
        url = `${process.env.REACT_APP_API_URL}/api/functions`;
        method = 'POST';
      }
      
      // Try the regular endpoint first
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(functionForm),
      });
      
      if (response.ok) {
        console.log(`Function ${editingFunction ? 'updated' : 'created'} successfully using regular endpoint`);
        showSnackbar(`Function ${editingFunction ? 'updated' : 'created'} successfully`);
        handleCloseDialog();
        fetchFunctions();
        return;
      }
      
      // If update fails, try the test endpoint
      if (editingFunction && method === 'PUT') {
        console.log('Regular update failed, trying test endpoint...');
        const testResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/test/update-function/${editingFunction.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(functionForm),
        });
        
        if (testResponse.ok) {
          console.log('Function updated successfully using test endpoint');
          showSnackbar('Function updated successfully');
          handleCloseDialog();
          fetchFunctions();
          return;
        }
        
        console.error('Both update endpoints failed');
        showSnackbar('Error updating function - please try again', 'error');
      } else {
        // Handle other failures
        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
        showSnackbar(errorData.error || `Error ${editingFunction ? 'updating' : 'creating'} function`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar(`Error ${editingFunction ? 'updating' : 'creating'} function`, 'error');
    }
  };

  const handleDeleteFunction = async (functionId) => {
    if (!window.confirm('Are you sure you want to delete this function?')) {
      return;
    }
    
    try {
      // Use the test endpoint directly as it's been fixed to work properly
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/test/delete-function/${functionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        console.log('Successfully deleted function');
        showSnackbar('Function deleted successfully');
        fetchFunctions(); // Refresh the function list
        return;
      }
      
      // If test endpoint fails, show error
      console.error(`Function deletion failed with status: ${response.status}`);
      showSnackbar('Error deleting function', 'error');
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error deleting function', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Custom Functions</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Function
        </Button>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 3 }}>
        Custom functions allow your agents to perform actions like making HTTP requests to external services.
        Functions you create here can be linked to any of your agents.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : functions.length === 0 ? (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <CardContent>
            <CodeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6">No Custom Functions</Typography>
            <Typography variant="body2" color="textSecondary">
              You haven't created any custom functions yet. Create your first function to extend your agents' capabilities.
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Function
            </Button>
          </CardActions>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {functions.map(func => (
            <Grid item xs={12} sm={6} md={4} key={func.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {func.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {func.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    Type: {func.function_type}
                  </Typography>
                  {func.function_type === 'http_request' && (
                    <Typography variant="caption" component="div">
                      Endpoint: {func.configuration.url}<br />
                      Method: {func.configuration.method}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleOpenDialog(func)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleDeleteFunction(func.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Function Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFunction ? 'Edit Function' : 'Create New Function'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Function Name"
                value={functionForm.name}
                onChange={handleInputChange('name')}
                fullWidth
                required
                helperText="The name the LLM will use to call this function"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Function Type</InputLabel>
                <Select
                  value={functionForm.function_type}
                  onChange={handleInputChange('function_type')}
                  label="Function Type"
                >
                  <MenuItem value="http_request">HTTP Request</MenuItem>
                  <MenuItem value="webhook">Webhook</MenuItem>
                  <MenuItem value="news">News API</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={functionForm.description}
                onChange={handleInputChange('description')}
                fullWidth
                multiline
                rows={3}
                required
                helperText="Describe what this function does and when the LLM should use it"
              />
            </Grid>
            
            {functionForm.function_type === 'http_request' && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Endpoint URL"
                    value={functionForm.configuration.url}
                    onChange={handleConfigChange('url')}
                    fullWidth
                    required
                    placeholder="https://example.com/api/endpoint"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Method</InputLabel>
                    <Select
                      value={functionForm.configuration.method}
                      onChange={handleConfigChange('method')}
                      label="Method"
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="DELETE">DELETE</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            {functionForm.function_type === 'webhook' && (
              <Grid item xs={12}>
                <TextField
                  label="Webhook URL"
                  value={functionForm.configuration?.url || ''}
                  onChange={handleConfigChange('url')}
                  fullWidth
                  required
                  placeholder="https://example.com/webhook"
                />
              </Grid>
            )}
            
            {functionForm.function_type === 'news' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>News Function Type</InputLabel>
                    <Select
                      value={functionForm.configuration.news_type || ''}
                      onChange={handleConfigChange('news_type')}
                      label="News Function Type"
                    >
                      <MenuItem value="topic">Get Topic News</MenuItem>
                      <MenuItem value="latest">Get Latest News</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {functionForm.configuration.news_type === 'topic' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Default Topic"
                      value={functionForm.configuration.default_topic || ''}
                      onChange={handleConfigChange('default_topic')}
                      fullWidth
                      helperText="Default topic to use if none is specified (e.g., tech, sports, politics)"
                    />
                  </Grid>
                )}
              </>
            )}
            
            {/* Parameters Section */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Function Parameters
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Define parameters that the LLM can pass to this function
                </Typography>
                
                {functionForm.parameters.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    {functionForm.parameters.map((param) => (
                      <Box 
                        key={param.name}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            {param.name} {param.required && <span style={{ color: 'red' }}>*</span>}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {param.description}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenParameterDialog(param)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteParameter(param.name)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2, bgcolor: 'background.paper', mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      No parameters defined
                    </Typography>
                  </Box>
                )}
                
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={() => handleOpenParameterDialog()}
                  size="small"
                >
                  Add Parameter
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveFunction}
          >
            {editingFunction ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parameter Dialog */}
      <Dialog
        open={openParameterDialog}
        onClose={handleCloseParameterDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentParameter.name ? 'Edit Parameter' : 'Add Parameter'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Parameter Name"
                value={currentParameter.name}
                onChange={handleParameterInputChange('name')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={currentParameter.description}
                onChange={handleParameterInputChange('description')}
                fullWidth
                required
                helperText="Describe what this parameter is for"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentParameter.required}
                    onChange={handleParameterInputChange('required')}
                  />
                }
                label="Required Parameter"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParameterDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveParameter}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FunctionsList; 